import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Users, Calendar, Clock, Plus, Eye, AlertTriangle, CheckCircle, Home, UserPlus } from 'lucide-react';

function CreateTeamShiftDialog({ team, open, onClose, services, onSuccess }) {
  const [form, setForm] = useState({
    service_id: '',
    shift_date: '',
    start_time: '09:00',
    end_time: '17:00',
    required_staff: 1,
    broadcast_to_team: true,
    notes: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.service_id || !form.shift_date) {
      toast.error('Please fill required fields');
      return;
    }
    setLoading(true);
    try {
      await api.post('/roster/teams/create-shift', {
        team_id: team.id,
        service_id: form.service_id,
        shift_date: form.shift_date,
        start_time: form.start_time,
        end_time: form.end_time,
        required_staff: form.required_staff,
        broadcast_to_team: form.broadcast_to_team,
        notes: form.notes,
      });
      toast.success('Shift created' + (form.broadcast_to_team ? ' and team notified' : ''));
      onSuccess();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create shift');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-500" />
            Create Team Shift
          </DialogTitle>
          <DialogDescription>
            Create a shift for {team?.name} ({team?.member_count} members)
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Service *</Label>
              <Select value={form.service_id} onValueChange={(v) => setForm({ ...form, service_id: v })}>
                <SelectTrigger data-testid="team-shift-service">
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent>
                  {services.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Shift Date *</Label>
              <Input
                type="date"
                value={form.shift_date}
                onChange={(e) => setForm({ ...form, shift_date: e.target.value })}
                data-testid="team-shift-date"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Start Time</Label>
              <Input
                type="time"
                value={form.start_time}
                onChange={(e) => setForm({ ...form, start_time: e.target.value })}
              />
            </div>
            <div>
              <Label>End Time</Label>
              <Input
                type="time"
                value={form.end_time}
                onChange={(e) => setForm({ ...form, end_time: e.target.value })}
              />
            </div>
            <div>
              <Label>Required Staff</Label>
              <Input
                type="number"
                min={1}
                value={form.required_staff}
                onChange={(e) => setForm({ ...form, required_staff: parseInt(e.target.value) || 1 })}
              />
            </div>
          </div>

          <div>
            <Label>Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Shift notes"
              rows={2}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="broadcast"
              checked={form.broadcast_to_team}
              onChange={(e) => setForm({ ...form, broadcast_to_team: e.target.checked })}
              className="rounded"
            />
            <Label htmlFor="broadcast" className="cursor-pointer">
              Notify team members about this shift
            </Label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} data-testid="team-shift-submit">
              {loading ? 'Creating...' : 'Create Shift'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TeamRosterDialog({ team, open, onClose }) {
  const [roster, setRoster] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && team) {
      setLoading(true);
      api.get(`/roster/teams/${team.id}/roster`)
        .then(res => setRoster(res.data.data))
        .catch(() => toast.error('Failed to load team roster'))
        .finally(() => setLoading(false));
    }
  }, [open, team]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-500" />
            {team?.name} - Weekly Roster
          </DialogTitle>
          <DialogDescription>
            {roster?.date_range?.start} to {roster?.date_range?.end}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-muted-foreground">Loading...</div>
        ) : roster ? (
          <div className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-3 text-center">
                  <p className="text-2xl font-bold">{roster.stats?.total_shifts || 0}</p>
                  <p className="text-xs text-muted-foreground">Total Shifts</p>
                </CardContent>
              </Card>
              <Card className={roster.stats?.open_shifts > 0 ? 'border-red-200' : ''}>
                <CardContent className="p-3 text-center">
                  <p className={`text-2xl font-bold ${roster.stats?.open_shifts > 0 ? 'text-red-600' : ''}`}>
                    {roster.stats?.open_shifts || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">Open Shifts</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 text-center">
                  <p className="text-2xl font-bold text-green-600">{roster.stats?.assigned_shifts || 0}</p>
                  <p className="text-xs text-muted-foreground">Assigned</p>
                </CardContent>
              </Card>
            </div>

            {/* Members */}
            <div>
              <h3 className="font-semibold text-sm text-muted-foreground mb-2">
                Team Members ({roster.members?.length || 0})
              </h3>
              <div className="flex flex-wrap gap-2">
                {roster.members?.map(m => (
                  <Badge key={m.staff_id} variant="outline" className="py-1">
                    {m.name}
                    <span className="ml-1 text-xs text-muted-foreground">({m.shifts_count} shifts)</span>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Linked Participants */}
            {roster.participants?.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm text-muted-foreground mb-2">
                  Linked Participants ({roster.participants.length})
                </h3>
                <div className="flex flex-wrap gap-2">
                  {roster.participants.map(p => (
                    <Badge key={p.participant_id} variant="secondary">{p.name}</Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Linked Houses */}
            {roster.houses?.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm text-muted-foreground mb-2">
                  Linked Houses ({roster.houses.length})
                </h3>
                <div className="flex flex-wrap gap-2">
                  {roster.houses.map(h => (
                    <Badge key={h.house_id} variant="outline">
                      <Home className="w-3 h-3 mr-1" />
                      {h.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Shifts */}
            <div>
              <h3 className="font-semibold text-sm text-muted-foreground mb-2">
                Shifts This Week ({roster.shifts?.length || 0})
              </h3>
              {roster.shifts?.length === 0 ? (
                <p className="text-sm text-muted-foreground">No shifts scheduled</p>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Time</TableHead>
                        <TableHead>Title</TableHead>
                        <TableHead>Assigned</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {roster.shifts?.slice(0, 20).map(s => (
                        <TableRow key={s.id}>
                          <TableCell className="font-mono text-sm">{s.date}</TableCell>
                          <TableCell className="font-mono text-sm">{s.start_time}-{s.end_time}</TableCell>
                          <TableCell>{s.title}</TableCell>
                          <TableCell>
                            {s.assigned_staff?.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {s.assigned_staff.map(a => (
                                  <Badge key={a.id} variant="outline" className="text-xs">{a.name}</Badge>
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-sm">Unassigned</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant={
                              s.status === 'ASSIGNED' ? 'default' :
                              s.status === 'OPEN' ? 'destructive' :
                              s.status === 'PARTIALLY_FILLED' ? 'secondary' : 'outline'
                            }>
                              {s.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export default function TeamRosterPage() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [createShiftDialog, setCreateShiftDialog] = useState(null);
  const [rosterDialog, setRosterDialog] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [teamsRes, servicesRes] = await Promise.all([
        api.get('/roster/teams/roster-summary'),
        api.get('/services/catalog'),
      ]);
      setTeams(teamsRes.data.data || []);
      setServices(servicesRes.data.data || []);
    } catch (e) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getTotalUnfilled = () => teams.reduce((sum, t) => sum + (t.unfilled_shifts || 0), 0);

  const getTypeLabel = (type) => {
    const labels = {
      'PARTICIPANT_TEAM': 'Participant',
      'SIL_HOUSE_TEAM': 'SIL House',
      'ACCOMMODATION_TEAM': 'Accommodation',
      'SUPPORT_COORDINATION': 'Coordination',
      'CLINICAL_TEAM': 'Clinical',
      'ROSTERING_TEAM': 'Rostering',
      'CUSTOM': 'Custom',
    };
    return labels[type] || type;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="team-roster-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Users className="w-6 h-6 text-purple-500" />
            Team Rostering
          </h1>
          <p className="text-muted-foreground mt-1">
            View and manage rosters by team
          </p>
        </div>
        {getTotalUnfilled() > 0 && (
          <Badge variant="destructive" className="text-sm px-3 py-1">
            {getTotalUnfilled()} Unfilled This Week
          </Badge>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Teams</p>
                <p className="text-2xl font-bold">{teams.length}</p>
              </div>
              <Users className="w-8 h-8 text-muted-foreground/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Members</p>
                <p className="text-2xl font-bold">{teams.reduce((s, t) => s + t.member_count, 0)}</p>
              </div>
              <UserPlus className="w-8 h-8 text-muted-foreground/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Shifts This Week</p>
                <p className="text-2xl font-bold">{teams.reduce((s, t) => s + t.shifts_this_week, 0)}</p>
              </div>
              <Calendar className="w-8 h-8 text-muted-foreground/50" />
            </div>
          </CardContent>
        </Card>
        <Card className={getTotalUnfilled() > 0 ? 'border-red-200 bg-red-50/50' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Unfilled</p>
                <p className={`text-2xl font-bold ${getTotalUnfilled() > 0 ? 'text-red-600' : ''}`}>
                  {getTotalUnfilled()}
                </p>
              </div>
              <AlertTriangle className={`w-8 h-8 ${getTotalUnfilled() > 0 ? 'text-red-400' : 'text-muted-foreground/50'}`} />
            </div>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading teams...</div>
      ) : teams.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">No teams found</p>
            <p className="text-sm text-muted-foreground mt-1">
              Create teams in the Teams module first
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {teams.map(t => (
            <Card key={t.id} className="hover:shadow-md transition-shadow" data-testid={`team-card-${t.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-lg">{t.name}</h3>
                      <Badge variant="outline">{getTypeLabel(t.type)}</Badge>
                      {t.unfilled_shifts > 0 && (
                        <Badge variant="destructive">{t.unfilled_shifts} unfilled</Badge>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-5 gap-4 mt-3">
                      <div>
                        <p className="text-xs text-muted-foreground">Members</p>
                        <p className="text-lg font-semibold">{t.member_count}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Participants</p>
                        <p className="text-lg font-semibold">{t.participant_count}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Houses</p>
                        <p className="text-lg font-semibold">{t.house_count}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Shifts (Week)</p>
                        <p className="text-lg font-semibold">{t.shifts_this_week}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Status</p>
                        {t.unfilled_shifts === 0 ? (
                          <span className="flex items-center gap-1 text-green-600 text-sm">
                            <CheckCircle className="w-4 h-4" /> Covered
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-red-600 text-sm">
                            <AlertTriangle className="w-4 h-4" /> Needs Staff
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 ml-4">
                    <Button size="sm" onClick={() => setCreateShiftDialog(t)} data-testid={`create-team-shift-${t.id}`}>
                      <Plus className="w-4 h-4 mr-1" />
                      Add Shift
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setRosterDialog(t)}>
                      <Eye className="w-4 h-4 mr-1" />
                      View Roster
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {createShiftDialog && (
        <CreateTeamShiftDialog
          team={createShiftDialog}
          open={!!createShiftDialog}
          onClose={() => setCreateShiftDialog(null)}
          services={services}
          onSuccess={fetchData}
        />
      )}

      {rosterDialog && (
        <TeamRosterDialog
          team={rosterDialog}
          open={!!rosterDialog}
          onClose={() => setRosterDialog(null)}
        />
      )}
    </div>
  );
}
